import React, { useState } from 'react';
import { Sparkles, Loader2, Check, CheckCheck, X, ArrowRight } from 'lucide-react';
import { Button } from './ui/button';
import { adminAPI } from '../services/api';

const ProductDescriptionDraftsModal = ({ missingCount, onClose, onApplied }) => {
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [applied, setApplied] = useState({});
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(null);

  const generate = async () => {
    setBusy(true); setError(''); setResult(null); setApplied({});
    try { setResult(await adminAPI.generateProductDescriptions(null, 10)); } catch (e) { setError(e.response?.data?.detail || 'Génération impossible pour le moment'); } finally { setBusy(false); }
  };

  const apply = async (d) => {
    setSaving(d.product_id);
    try {
      await adminAPI.setProductDescription(d.product_id, d.description);
      setApplied(a => ({ ...a, [d.product_id]: true }));
      onApplied && onApplied(d.product_id, d.description);
    } catch (e) {
      setError(e.response?.data?.detail || 'Enregistrement impossible');
    } finally {
      setSaving(null);
    }
  };
  const applyAll = async () => { for (const d of result.drafts) if (!applied[d.product_id]) await apply(d); };
  const pending = result ? result.drafts.filter(d => !applied[d.product_id]) : [];
  const allApplied = result && pending.length === 0;

  return (
    <div className="fixed inset-0 z-[80] bg-black/50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-4xl max-h-[92vh] flex flex-col" onClick={(e) => e.stopPropagation()} data-testid="product-descriptions-modal">
        <div className="p-4 border-b flex items-center justify-between bg-[#1e3a5f] text-white rounded-t-xl">
          <h2 className="font-semibold flex items-center gap-2"><Sparkles size={18} className="text-orange-400" /> Descriptions produit — brouillons IA</h2>
          <button type="button" onClick={onClose} className="text-white/80 hover:text-white" data-testid="product-descriptions-close"><X size={20} /></button>
        </div>
        <div className="p-4 overflow-y-auto flex-1 space-y-3">
          <p className="text-sm text-gray-600">
            <strong data-testid="product-descriptions-missing">{missingCount}</strong> produit{missingCount > 1 ? 's' : ''} sans description. L'IA rédige par lots de 10 à partir des données réelles (nom, marque, référence, type, compatibilités) : textes uniques, utiles, avec conseils de montage. Rien n'est publié avant « Appliquer ».
          </p>
          {!result && (
            <Button onClick={generate} disabled={busy || missingCount === 0} data-testid="product-descriptions-generate" className="bg-orange-500 hover:bg-orange-600">
              {busy ? <Loader2 size={16} className="mr-2 animate-spin" /> : <Sparkles size={16} className="mr-2" />} Générer le lot suivant ({Math.min(10, missingCount)} produit{Math.min(10, missingCount) > 1 ? 's' : ''})
            </Button>
          )}
          {error && <p className="text-sm text-red-600" data-testid="product-descriptions-error">{error}</p>}
          {result && (
            <>
              <div className="flex flex-wrap items-center justify-between gap-2">
                <p className="text-sm text-gray-700" data-testid="product-descriptions-message">{result.message}{result.remaining ? ` · encore ${result.remaining} produit${result.remaining > 1 ? 's' : ''} à traiter` : ''}</p>
                <div className="flex gap-2">
                  {pending.length > 1 && <Button size="sm" variant="outline" onClick={applyAll} disabled={!!saving} data-testid="product-descriptions-apply-all" className="border-green-500 text-green-700"><CheckCheck size={14} className="mr-1" /> Tout appliquer ({pending.length})</Button>}
                  {allApplied && result.remaining > 0 && <Button size="sm" onClick={generate} disabled={busy} data-testid="product-descriptions-next" className="bg-orange-500 hover:bg-orange-600">{busy ? <Loader2 size={14} className="mr-1 animate-spin" /> : <ArrowRight size={14} className="mr-1" />} Lot suivant</Button>}
                </div>
              </div>
              {result.drafts.map(d => (
                <div key={d.product_id} className={`border rounded-lg p-3 flex gap-3 ${applied[d.product_id] ? 'border-green-300 bg-green-50/40' : 'border-gray-200'}`} data-testid={`product-description-draft-${d.product_id}`}>
                  <img src={d.image} alt="" className="w-14 h-14 object-contain bg-white border rounded flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between gap-2">
                      <p className="text-sm font-semibold text-gray-900 truncate">{d.name} <span className="text-xs font-normal text-gray-500">· {d.brand} · Réf. {d.ref}</span></p>
                      {applied[d.product_id]
                        ? <span className="text-xs text-green-700 inline-flex items-center gap-1 whitespace-nowrap"><Check size={12} /> Appliqué</span>
                        : <Button size="sm" onClick={() => apply(d)} disabled={saving === d.product_id} data-testid={`product-description-apply-${d.product_id}`} className="h-7 bg-green-600 hover:bg-green-700 whitespace-nowrap">{saving === d.product_id ? <Loader2 size={13} className="mr-1 animate-spin" /> : <Check size={13} className="mr-1" />} Appliquer</Button>}
                    </div>
                    <div className="text-xs text-gray-700 mt-1.5 space-y-1.5 whitespace-pre-line">{d.description}</div>
                    <p className="text-[11px] text-gray-400 mt-1">{d.description.split(/\s+/).length} mots</p>
                  </div>
                </div>
              ))}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default ProductDescriptionDraftsModal;
